import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Link, Outlet, useNavigate } from 'react-router-dom';

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Vérification du rôle admin côté frontend
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error("Accès refusé : rôle administrateur requis");
      navigate('/');
    }
  }, [user, navigate]);

  // États pour les listes
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Chargement des données admin
  const fetchData = async () => {
    try {
      const [u, s, g, f, b, c] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/services'),
        axios.get('/api/groupes'),
        axios.get('/api/familles'),
        axios.get('/api/beneficiaires'),
        axios.get('/api/contacts'),
      ]);
      setUsers(u.data);
      setServices(s.data);
      setGroupes(g.data);
      setFamilles(f.data);
      setBeneficiaires(b.data);
      setContacts(c.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Suppression utilisateur (exemple, demande confirmation)
  const handleDeleteUser = async (id) => {
    if (!window.confirm("Confirmer la suppression de cet utilisateur ?")) return;
    try {
      await axios.delete(`/api/users/${id}`);
      toast.success("Utilisateur supprimé");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10">
      <header className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Panneau d’Administration</h1>
        <button
          onClick={logout}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
        >
          Déconnexion
        </button>
      </header>

      {/* Navigation rapide */}
      <nav className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-6 gap-4">
        <Link to="/admin/users" className="bg-indigo-600 text-white rounded p-4 text-center hover:bg-indigo-700">
          Gestion des Utilisateurs ({users.length})
        </Link>
        <Link to="/admin/services" className="bg-blue-600 text-white rounded p-4 text-center hover:bg-blue-700">
          Services ({services.length})
        </Link>
        <Link to="/admin/groupes" className="bg-green-600 text-white rounded p-4 text-center hover:bg-green-700">
          Groupes ({groupes.length})
        </Link>
        <Link to="/admin/familles" className="bg-yellow-600 text-white rounded p-4 text-center hover:bg-yellow-700">
          Familles ({familles.length})
        </Link>
        <Link to="/admin/beneficiaires" className="bg-pink-600 text-white rounded p-4 text-center hover:bg-pink-700">
          Bénéficiaires ({beneficiaires.length})
        </Link>
        <Link to="/admin/contacts" className="bg-gray-600 text-white rounded p-4 text-center hover:bg-gray-700">
          Contacts ({contacts.length})
        </Link>
      </nav>

      {/* Aperçu simple des utilisateurs (exemple) */}
      <section>
        <h2 className="text-2xl font-semibold mb-4">Utilisateurs (Aperçu)</h2>
        <table className="w-full border rounded overflow-hidden">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="p-2">ID</th>
              <th className="p-2">Nom</th>
              <th className="p-2">Email</th>
              <th className="p-2">Rôle</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.slice(0, 5).map(u => (
              <tr key={u.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="p-2">{u.id}</td>
                <td className="p-2">{u.nom}</td>
                <td className="p-2">{u.email}</td>
                <td className="p-2 capitalize">{u.role}</td>
                <td className="p-2">
                  <button
                    onClick={() => handleDeleteUser(u.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Supprimer utilisateur"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
            {users.length > 5 && (
              <tr>
                <td colSpan="5" className="text-center p-2 text-sm text-gray-500">... et plus</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Section Outlet pour les sous-routes admin */}
      <Outlet />
    </div>
  );
}
